import { useEffect, useState } from 'react';
import api from '../services/api';

interface ShadowStats {
  total: number;
  divergences: {
    none: number;
    flip_abstain: number;
    flip_enter: number;
    size_delta: number;
  };
  v2Actions: { enter: number; abstain: number };
  meanSizeDeltaContracts: number;
  cumulativeSizeDelta: number;
  lastDecisions: Array<{
    signalId: string;
    ts: string;
    v2Action: string;
    v2Contracts: number;
    v2Fraction: number;
    v2KellyRaw: number;
    divergence: string;
    sizeDeltaContracts: number;
    liveContracts: number;
    rationale: string[];
  }>;
}

interface MetaSuggestion {
  parameter: string;
  currentValue: number;
  suggestedValue: number;
  delta: number;
  rationale: string;
  sampleSize: number;
  emittedAt: string;
}

const POLL_MS = 10_000;

const pct = (n: number, d: number): string =>
  d === 0 ? '0.0%' : `${((n / d) * 100).toFixed(1)}%`;

export default function TomiV2ShadowPanel() {
  const [stats, setStats] = useState<ShadowStats | null>(null);
  const [suggestions, setSuggestions] = useState<MetaSuggestion[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [s, m] = await Promise.all([
          api.get('/tomi-engine/v2/shadow/stats'),
          api.get('/tomi-engine/v2/meta/suggestions'),
        ]);
        if (cancelled) return;
        setStats(s.data?.stats ?? null);
        setSuggestions(m.data?.suggestions ?? []);
        setError(null);
      } catch (e: unknown) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : 'request failed';
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const handleReset = async () => {
    if (!confirm('Reset all v2 shadow stats?')) return;
    try {
      await api.post('/tomi-engine/v2/shadow/reset');
      setStats(null);
    } catch (e) {
      alert(`reset failed: ${e instanceof Error ? e.message : 'unknown'}`);
    }
  };

  if (loading) return <div className="p-4 text-sm text-gray-400">loading v2 shadow stats…</div>;
  if (error) return <div className="p-4 text-sm text-red-400">{error}</div>;
  if (!stats) return null;

  const total = stats.total || 1;

  return (
    <div className="border border-gray-700 rounded-lg p-4 bg-gray-900/50 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-emerald-300">
          Tomi v2 — Shadow Mode
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{stats.total} samples</span>
          <button
            onClick={handleReset}
            className="text-xs px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-200"
          >
            reset
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Stat label="agree" value={pct(stats.divergences.none, total)} sub={`${stats.divergences.none}`} />
        <Stat
          label="v2 abstains (live entered)"
          value={pct(stats.divergences.flip_abstain, total)}
          sub={`${stats.divergences.flip_abstain}`}
          tone="warn"
        />
        <Stat
          label="v2 enters (live abstained)"
          value={pct(stats.divergences.flip_enter, total)}
          sub={`${stats.divergences.flip_enter}`}
          tone="warn"
        />
        <Stat
          label="size delta"
          value={pct(stats.divergences.size_delta, total)}
          sub={`mean ${stats.meanSizeDeltaContracts.toFixed(2)} ct`}
        />
      </div>

      {suggestions.length > 0 && (
        <div className="border border-amber-700/40 bg-amber-900/10 rounded p-3">
          <div className="text-sm font-medium text-amber-300 mb-2">
            meta-learner suggestions ({suggestions.length})
          </div>
          <ul className="space-y-1 text-xs text-amber-100">
            {suggestions.map((s, i) => (
              <li key={i}>
                <span className="font-mono">{s.parameter}</span>:{' '}
                {s.currentValue.toFixed(3)} → {s.suggestedValue.toFixed(3)}{' '}
                <span className="text-amber-300">({s.delta >= 0 ? '+' : ''}{s.delta.toFixed(3)})</span>
                <div className="text-amber-200/70 ml-2">{s.rationale}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">
          last 12 decisions
        </div>
        <div className="space-y-1 text-xs font-mono max-h-64 overflow-y-auto">
          {stats.lastDecisions
            .slice(-12)
            .reverse()
            .map((d) => (
              <div
                key={d.signalId + d.ts}
                className={`flex items-center justify-between gap-2 px-2 py-1 rounded ${
                  d.divergence === 'none'
                    ? 'bg-gray-800/40'
                    : 'bg-amber-900/20 border border-amber-800/30'
                }`}
              >
                <span className="text-gray-400">{d.ts.slice(11, 19)}</span>
                <span className="text-gray-200 truncate" title={d.signalId}>
                  {d.signalId.slice(0, 10)}
                </span>
                <span className="text-gray-300">
                  live={d.liveContracts} v2={d.v2Contracts}
                </span>
                <span
                  className={
                    d.divergence === 'none'
                      ? 'text-gray-500'
                      : 'text-amber-300'
                  }
                >
                  {d.divergence}
                </span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'warn';
}) {
  const valueColor = tone === 'warn' ? 'text-amber-300' : 'text-emerald-300';
  return (
    <div className="bg-gray-800/40 rounded p-2">
      <div className="text-xs text-gray-400">{label}</div>
      <div className={`text-lg font-semibold ${valueColor}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500">{sub}</div>}
    </div>
  );
}
